import { getImageUrl, getVideoUrl } from './mediaUrl';

// Font URLs to preload
const fontsToPreload = [
    '/fonts/font.woff2',
    '/fonts/font-med.woff2',
    '/fonts/font-bold.woff2',
    '/fonts/font-gonz.woff2',
];

// Images to preload (using getImageUrl for proper CDN handling)
const imagesToPreload = [
    'logo.svg',
    'icon-account.svg',
    'settings.svg',
    'icon-chatText.svg',
    'icon-x.svg',
    'icon-search.svg',
    'icon-captions.svg',
    'mask-bjo.svg',
    'mask-you.svg',
    'mask-avatar.svg',
    'cornerLeftMask.svg',
    'highlight.svg',
    'cursor.svg',
];

// Videos to preload
const videosToPreload = [
    'baking.mp4',
];

/**
 * Preload a single image
 */
const preloadImage = (url) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ url, success: true });
        img.onerror = () => resolve({ url, success: false }); // Resolve even on error to not block
        img.src = url;
    });
};

/**
 * Preload a single font using FontFace API
 */
const preloadFont = (url, fontFamily) => {
    return new Promise((resolve) => {
        // Use FontFace API if available
        if ('FontFace' in window) {
            const font = new FontFace(fontFamily, `url(${url})`);
            font.load()
                .then(() => {
                    document.fonts.add(font);
                    resolve({ url, success: true });
                })
                .catch(() => {
                    resolve({ url, success: false });
                });
        } else {
            // Fallback: just fetch the font file
            fetch(url)
                .then(() => resolve({ url, success: true }))
                .catch(() => resolve({ url, success: false }));
        }
    });
};

/**
 * Preload a single video
 */
const preloadVideo = (url) => {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        video.preload = 'auto';

        const handleLoad = () => {
            cleanup();
            resolve({ url, success: true });
        };

        const handleError = () => {
            cleanup();
            resolve({ url, success: false });
        };

        const cleanup = () => {
            video.removeEventListener('canplaythrough', handleLoad);
            video.removeEventListener('error', handleError);
        };

        video.addEventListener('canplaythrough', handleLoad);
        video.addEventListener('error', handleError);

        // Set a timeout to resolve anyway after 10 seconds
        setTimeout(() => {
            cleanup();
            resolve({ url, success: false, timeout: true });
        }, 10000);

        video.src = url;
        video.load();
    });
};

/**
 * Preload all assets (images, fonts, videos)
 * @param {Function} onProgress - Optional callback for progress updates (0-100)
 * @returns {Promise} Resolves when all assets are loaded
 */
export const preloadAllAssets = (onProgress) => {
    const imageUrls = imagesToPreload.map(img => getImageUrl(img));
    const videoUrls = videosToPreload.map(vid => getVideoUrl(vid));

    // Map font URLs to their font families
    const fontData = [
        { url: fontsToPreload[0], family: 'Font' },
        { url: fontsToPreload[1], family: 'Font Med' },
        { url: fontsToPreload[2], family: 'Font Bold' },
        { url: fontsToPreload[3], family: 'Gonz' },
    ];

    const totalAssets = imageUrls.length + fontData.length + videoUrls.length;
    let loadedCount = 0;

    const updateProgress = () => {
        loadedCount++;
        if (onProgress) {
            onProgress(Math.round((loadedCount / totalAssets) * 100));
        }
    };

    // Create all promises
    const imagePromises = imageUrls.map(url =>
        preloadImage(url).then(result => {
            updateProgress();
            return result;
        })
    );

    const fontPromises = fontData.map(({ url, family }) =>
        preloadFont(url, family).then(result => {
            updateProgress();
            return result;
        })
    );

    const videoPromises = videoUrls.map(url =>
        preloadVideo(url).then(result => {
            updateProgress();
            return result;
        })
    );

    return Promise.all([
        ...imagePromises,
        ...fontPromises,
        ...videoPromises,
    ]);
};

/**
 * Preload only critical assets (images and fonts, skip videos)
 * Faster initial load, videos can load in background
 */
export const preloadCriticalAssets = (onProgress) => {
    const imageUrls = imagesToPreload.map(img => getImageUrl(img));

    const fontData = [
        { url: fontsToPreload[0], family: 'Font' },
        { url: fontsToPreload[1], family: 'Font Med' },
        { url: fontsToPreload[2], family: 'Font Bold' },
        { url: fontsToPreload[3], family: 'Gonz' },
    ];

    const totalAssets = imageUrls.length + fontData.length;
    let loadedCount = 0;

    const updateProgress = () => {
        loadedCount++;
        if (onProgress) {
            onProgress(Math.round((loadedCount / totalAssets) * 100));
        }
    };

    const imagePromises = imageUrls.map(url =>
        preloadImage(url).then(result => {
            updateProgress();
            return result;
        })
    );

    const fontPromises = fontData.map(({ url, family }) =>
        preloadFont(url, family).then(result => {
            updateProgress();
            return result;
        })
    );

    return Promise.all([
        ...imagePromises,
        ...fontPromises,
    ]);
};

/**
 * Preload video in background (call after critical assets are loaded)
 */
export const preloadVideoInBackground = () => {
    const videoUrls = videosToPreload.map(vid => getVideoUrl(vid));
    return Promise.all(videoUrls.map(url => preloadVideo(url)));
};

export default preloadAllAssets;
